import { normalizeFormDateStringToIso } from '~/utils/dateFormatter';

import { parseNonEmptyStrings } from '../parseHelpers';

import { createDefaultCard } from './defaults';
import type { WhatsNewCarouselCard, WhatsNewCarouselPayloadBuilderState } from './types';

const PREVIEW_REQUIRED = '[required]';

function previewRequiredValue(value: string): string {
  return value.trim() ? value.trim() : PREVIEW_REQUIRED;
}

function previewDateTime(value: string): string {
  return value.trim() ? normalizeFormDateStringToIso(value) : PREVIEW_REQUIRED;
}

function buildCard(card: WhatsNewCarouselCard): Record<string, unknown> {
  const imageFit = card.imageOptionsFit.trim();
  const optimizelyFeatureFlag = card.optimizelyFeatureFlag.trim();

  return {
    endAt: normalizeFormDateStringToIso(card.endAt),
    ctaUrl: card.ctaUrl.trim(),
    imageId: card.imageId.trim(),
    startAt: normalizeFormDateStringToIso(card.startAt),
    isActive: card.isActive,
    'title-i18n': card.title.trim(),
    ...(imageFit ? { imageOptions: { fit: imageFit } } : {}),
    'ctaLabel-i18n': card.ctaLabel.trim(),
    'overline-i18n': card.overline.trim(),
    enabledRegions: parseNonEmptyStrings(card.enabledRegions),
    'description-i18n': card.description.trim(),
    ...(optimizelyFeatureFlag ? { optimizelyFeatureFlag } : {}),
  };
}

function buildPreviewCard(card: WhatsNewCarouselCard): Record<string, unknown> {
  const imageFit = card.imageOptionsFit.trim();
  const optimizelyFeatureFlag = card.optimizelyFeatureFlag.trim();

  return {
    endAt: previewDateTime(card.endAt),
    ctaUrl: previewRequiredValue(card.ctaUrl),
    imageId: previewRequiredValue(card.imageId),
    startAt: previewDateTime(card.startAt),
    isActive: card.isActive,
    'title-i18n': previewRequiredValue(card.title),
    ...(imageFit ? { imageOptions: { fit: imageFit } } : {}),
    'ctaLabel-i18n': previewRequiredValue(card.ctaLabel),
    'overline-i18n': previewRequiredValue(card.overline),
    enabledRegions: parseNonEmptyStrings(card.enabledRegions),
    'description-i18n': previewRequiredValue(card.description),
    ...(optimizelyFeatureFlag ? { optimizelyFeatureFlag } : {}),
  };
}

export function buildWhatsNewCarouselPayload(state: WhatsNewCarouselPayloadBuilderState): Record<string, unknown> {
  return {
    cards: state.cards.map(buildCard),
  };
}

export function buildWhatsNewCarouselPreviewPayload(
  state: WhatsNewCarouselPayloadBuilderState,
): Record<string, unknown> {
  const cards = state.cards.length > 0 ? state.cards : [createDefaultCard()];

  return {
    cards: cards.map(buildPreviewCard),
  };
}

export function serializeWhatsNewCarouselPayload(state: WhatsNewCarouselPayloadBuilderState): string {
  return JSON.stringify(buildWhatsNewCarouselPayload(state), null, 2);
}

export function serializeWhatsNewCarouselPreviewPayload(state: WhatsNewCarouselPayloadBuilderState): string {
  return JSON.stringify(buildWhatsNewCarouselPreviewPayload(state), null, 2);
}

export function whatsNewCarouselPayloadDownloadFilename(): string {
  return 'whats-new-carousel-payload.txt';
}
